import { useCallback, useEffect, useRef, useState } from "react";
import type { OnboardingState } from "@/types/question";

interface QuestionSnapshot {
  onboardingState: OnboardingState;
  selectedAnswer: number | undefined;
  selectedCheckboxAnswers: number[];
  freeTextAnswer: string;
}

interface UseQuestionnaireBackNavigationOptions {
  enabled: boolean;
  onboardingState: OnboardingState | null;
  selectedAnswer: number | undefined;
  selectedCheckboxAnswers: number[];
  freeTextAnswer: string;
  setOnboardingState: React.Dispatch<
    React.SetStateAction<OnboardingState | null>
  >;
  setSelectedAnswer: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  setSelectedCheckboxAnswers: React.Dispatch<React.SetStateAction<number[]>>;
  setFreeTextAnswer: React.Dispatch<React.SetStateAction<string>>;
  skipFreeTextResetRef: React.MutableRefObject<boolean>;
}

export const useQuestionnaireBackNavigation = ({
  enabled,
  onboardingState,
  selectedAnswer,
  selectedCheckboxAnswers,
  freeTextAnswer,
  setOnboardingState,
  setSelectedAnswer,
  setSelectedCheckboxAnswers,
  setFreeTextAnswer,
  skipFreeTextResetRef,
}: UseQuestionnaireBackNavigationOptions) => {
  const snapshotsRef = useRef<QuestionSnapshot[]>([]);
  const [canGoBack, setCanGoBack] = useState(false);

  const pushHistoryEntry = useCallback(() => {
    window.history.pushState(
      { questionnaireStep: snapshotsRef.current.length },
      "",
      window.location.href,
    );
  }, []);

  const restorePrevious = useCallback(() => {
    const snapshot = snapshotsRef.current.pop();

    if (!snapshot) {
      return false;
    }

    setOnboardingState(snapshot.onboardingState);
    setSelectedAnswer(snapshot.selectedAnswer);
    setSelectedCheckboxAnswers(snapshot.selectedCheckboxAnswers);
    setFreeTextAnswer(snapshot.freeTextAnswer);
    skipFreeTextResetRef.current = true;
    setCanGoBack(snapshotsRef.current.length > 0);
    return true;
  }, [
    setFreeTextAnswer,
    setOnboardingState,
    setSelectedAnswer,
    setSelectedCheckboxAnswers,
    skipFreeTextResetRef,
  ]);

  const pushSnapshot = useCallback(() => {
    if (!onboardingState) {
      return;
    }

    snapshotsRef.current.push({
      onboardingState,
      selectedAnswer,
      selectedCheckboxAnswers,
      freeTextAnswer,
    });
    setCanGoBack(true);
    pushHistoryEntry();
  }, [
    freeTextAnswer,
    onboardingState,
    pushHistoryEntry,
    selectedAnswer,
    selectedCheckboxAnswers,
  ]);

  const goToPreviousQuestion = useCallback(() => {
    if (!restorePrevious()) {
      return;
    }

    pushHistoryEntry();
  }, [pushHistoryEntry, restorePrevious]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    pushHistoryEntry();

    const handlePopState = () => {
      if (restorePrevious()) {
        pushHistoryEntry();
        return;
      }

      // On the first question, keep the user on this page.
      pushHistoryEntry();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [enabled, pushHistoryEntry, restorePrevious]);

  return {
    canGoBack,
    goToPreviousQuestion,
    pushSnapshot,
  };
};
